import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  BatteryCharging,
  BatteryFull,
  Bluetooth,
  BluetoothOff,
  BluetoothSearching,
  Check,
  Circle,
  RotateCw,
  Minus,
  MinusCircle,
  Lightbulb,
  Eraser,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { showToast } from "utils/toast";
import betaAPI from "v2/api";
import useWearableLabels from "v2/hooks/useWearableLabels";
import { byFieldLabel } from "utils/wearableLabels";
import {
  bleTargetFromName,
  clearOverBle,
  dfuLog,
  identifyOverBle,
  isBluetoothAvailable,
  onBluetoothAvailabilityChanged,
  scanForDfuDevices,
  stopBleScan,
  updateFirmwareOverBle,
  watchWearableAdverts,
} from "utils/bleDfu";
import { baseVersionOf } from "utils/firmwareRelease";
import {
  canClear,
  groupByVersion,
  productVersionsFrom,
  versionLabel,
} from "utils/wearableAdvert";
import { describeError } from "./UpdateFirmwareFlow";
import FirmwareSource, { formatKb } from "./FirmwareSource";
import ClearProgress from "./common/ClearProgress";
import ConfirmDialog from "./common/ConfirmDialog";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const SCAN_MS = 10000;
// Between wearables: lets the last one's bootloader drop off the air before
// the next scan starts.
const BETWEEN_MS = 1500;
// Between wearables, while earlier ones are still unconfirmed: listen to
// adverts this long instead, so units that are back show it during the batch
// rather than only at the end. It doesn't wait for them; the rest are picked
// up at the next gap or once the batch ends.
const QUICK_CHECK_MS = 3000;

const primaryGradient = {
  background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
};
const btnSecondary =
  "flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50";

const KIND_BADGE = {
  app: "bg-[#EFF6FF] text-[#1D4ED8]",
  bootloader: "bg-[#FEF3E7] text-[#B45309]",
  stock: "bg-[#FEF3E7] text-[#B45309]",
};

// Errors a second attempt can't fix: the device refused the package itself.
const FINAL_ERRORS = ["rejected", "package"];

/**
 * Firmware update over Bluetooth with no USB connection: scan for wearables
 * advertising for an update ("SIT-XXXX", or already in the bootloader), pick
 * one or more, and send them one zip. Wearables update one after another —
 * Electron answers one Bluetooth request at a time, and the bootloader is
 * found by name, so running them side by side could mix units up.
 *
 * Docked wearables (firmware v0.9.0+) advertise their firmware version. With
 * `showVersions` (superadmins) the list shows each unit's version and battery,
 * counts units per version, can select every unit not on the chosen package's
 * version, and after an update checks each unit now reports it. Otherwise
 * success means the bootloader took the whole image.
 */
const BluetoothDfuFlow = ({ onExit, onBusyChange, showVersions = false }) => {
  const { t } = useTranslation();
  const labels = useWearableLabels();
  const [pkg, setPkg] = useState(null);
  const [pkgError, setPkgError] = useState(null);
  const [btAvailable, setBtAvailable] = useState(null);

  const [scanning, setScanning] = useState(false);
  const [devices, setDevices] = useState([]); // [{ id, name, target }]
  const [selected, setSelected] = useState(() => new Set());
  // name → parsed advert (or null), from the advert watcher.
  const [adverts, setAdverts] = useState(() => new Map());
  const [productVersions, setProductVersions] = useState(() => new Map());
  // Identify (blink white 5 s): the scanned id being asked, and the last
  // result per id ("done" | "failed").
  const [identifyingId, setIdentifyingId] = useState(null);
  const [identified, setIdentified] = useState({});

  // [{ id, name, target, status, stage, progress, error, failedStage, retrying }]
  // status: queued | running | done | failed | skipped
  const [jobs, setJobs] = useState(null);
  const [running, setRunning] = useState(false);
  // A quick advert check between two transfers (QUICK_CHECK_MS).
  const [checking, setChecking] = useState(false);
  const [stopping, setStopping] = useState(false);
  const stopRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      stopRef.current = true;
      stopBleScan();
    };
  }, []);

  useEffect(() => {
    onBusyChange?.(running);
  }, [running, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  useEffect(() => {
    let alive = true;
    isBluetoothAvailable().then((ok) => alive && setBtAvailable(ok));
    const unsubscribe = onBluetoothAvailabilityChanged(setBtAvailable);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);

  const scan = async () => {
    if (scanning || running) return;
    setScanning(true);
    setDevices([]);
    try {
      const found = await scanForDfuDevices({
        durationMs: SCAN_MS,
        onDevices: (list) => aliveRef.current && setDevices(list),
      });
      if (!aliveRef.current) return;
      setDevices(found);
      // Keep the selection for wearables that are still there.
      setSelected(
        (prev) => new Set(found.filter((d) => prev.has(d.id)).map((d) => d.id))
      );
    } catch (e) {
      dfuLog(`scan failed: ${e.message}`);
    } finally {
      if (aliveRef.current) setScanning(false);
    }
  };

  // Production releases, to show a unit's product version (v1.4.0) rather
  // than its internal one (v0.9.0).
  useEffect(() => {
    if (!showVersions) return;
    let alive = true;
    betaAPI
      .getWearableFirmwareReleases({ channel: "production" })
      .then(
        ({ data }) =>
          alive && setProductVersions(productVersionsFrom(data?.releases))
      )
      .catch((e) => console.error("Failed to list firmware releases", e));
    return () => {
      alive = false;
    };
  }, [showVersions]);

  // Watch adverts, but not during a transfer: an active scan takes radio time
  // from the update's connection, and the Mac then drops firmware packets
  // (seen as "object ended at 3172, expected 4096" on every unit). In a batch
  // it only listens in the quick check between two units; docked units keep
  // advertising, so the rest are reported once the batch ends.
  useEffect(() => {
    if (!showVersions || !btAvailable || (running && !checking))
      return undefined;
    return watchWearableAdverts(setAdverts);
  }, [showVersions, btAvailable, running, checking]);

  // A finished unit is back once it advertises again after its update: keep
  // the version it reports on the job, so the result survives the watcher
  // pausing for the next transfer.
  useEffect(() => {
    if (!jobs?.some((j) => j.status === "done")) return;
    setJobs((prev) => {
      let changed = false;
      const next = prev.map((j) => {
        const advert = adverts.get(j.name);
        if (
          j.status !== "done" ||
          !advert?.version ||
          advert.seenAt <= j.finishedAt ||
          advert.version === j.backOn
        ) {
          return j;
        }
        changed = true;
        return { ...j, backOn: advert.version };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line
  }, [adverts]);

  // Scan once Bluetooth is known to be on.
  const didScanRef = useRef(false);
  useEffect(() => {
    if (btAvailable && !didScanRef.current) {
      didScanRef.current = true;
      scan();
    }
    // eslint-disable-next-line
  }, [btAvailable]);

  const choosePackage = (next) => {
    setPkgError(null);
    setPkg(next);
  };
  const packageFailed = (message) => {
    setPkg(null);
    setPkgError(message);
  };

  // Units the advert watcher sees but the scan didn't (it stops after
  // SCAN_MS) are listed too: an app-mode unit is updated by name.
  const listed = useMemo(() => {
    // The number printed on each case, matched on the name's MAC digits.
    // Sorted by that number, so the batch updates 001, 002, … in order.
    const withLabel = (d) => ({ ...d, label: labels.forName(d.name) });
    if (!showVersions) return devices.map(withLabel).sort(byFieldLabel);
    const names = new Set(devices.map((d) => d.name));
    const extra = [...adverts.keys()]
      .filter((name) => !names.has(name))
      .map((name) => ({
        id: name,
        name,
        target: bleTargetFromName(name),
        fromAdvert: true,
      }))
      .filter((d) => d.target);
    return [...devices, ...extra]
      .map((d) => ({
        ...withLabel(d),
        advert: d.target.kind === "app" ? adverts.get(d.name) || null : null,
      }))
      .sort(byFieldLabel);
  }, [devices, adverts, showVersions, labels]);

  const targetVersion = pkg
    ? pkg.release?.baseVersion || baseVersionOf(pkg.version)
    : null;
  const appUnits = listed.filter((d) => d.target.kind === "app");
  const groups = groupByVersion(appUnits);
  const outdatedIds = targetVersion
    ? appUnits
        .filter((d) => d.advert?.version !== targetVersion)
        .map((d) => d.id)
    : [];
  const selectOutdated = () => setSelected(new Set(outdatedIds));
  const offerOutdated =
    showVersions &&
    outdatedIds.length > 0 &&
    !outdatedIds.every((id) => selected.has(id));

  // With versions: one collapsible group per version (newest first, unknown
  // last), then units already in their bootloader. Without: one flat list.
  const sections = showVersions
    ? [
        ...groups.map((g) => {
          const builds = new Set(
            g.ids.map((id) => listed.find((d) => d.id === id)?.advert?.build)
          );
          const [build] = builds;
          return {
            key: g.version || "unknown",
            ids: g.ids,
            header: {
              label: g.version
                ? versionLabel(g.version, productVersions)
                : t("deviceTools.bleBatch.versionUnknown"),
              muted: !g.version,
              build: builds.size === 1 && build !== "production" ? build : null,
              current: !!g.version && g.version === targetVersion,
            },
          };
        }),
        ...(listed.some((d) => d.target.kind !== "app")
          ? [
              {
                key: "bootloader",
                ids: listed
                  .filter((d) => d.target.kind !== "app")
                  .map((d) => d.id),
                header: { label: t("deviceTools.bleBatch.kinds.bootloader") },
              },
            ]
          : []),
      ]
    : [{ key: "all", ids: listed.map((d) => d.id), header: null }];

  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // A group's checkbox: select the whole group, or clear it when it's full.
  const toggleGroup = (ids) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const all = ids.every((id) => next.has(id));
      ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
      return next;
    });

  const identify = async (d) => {
    if (scanning || running || identifyingId) return;
    setIdentifyingId(d.id);
    let result = "done";
    try {
      // A unit only the advert watcher saw has no scanned id to pin.
      await identifyOverBle({
        name: d.name,
        deviceId: d.fromAdvert ? null : d.id,
      });
    } catch (e) {
      dfuLog(`identify ${d.name} failed: ${e.message}`);
      result = "failed";
    }
    if (!aliveRef.current) return;
    setIdentifyingId(null);
    setIdentified((prev) => ({ ...prev, [d.id]: result }));
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected =
    listed.length > 0 && listed.every((d) => selected.has(d.id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(listed.map((d) => d.id)));

  const patchJob = (id, patch) =>
    aliveRef.current &&
    setJobs((prev) => prev?.map((j) => (j.id === id ? { ...j, ...patch } : j)));

  const updateOne = async (job) => {
    // A unit already in its bootloader (or left there by a failed attempt)
    // goes straight to the transfer.
    let fromBootloader = job.target.kind !== "app" || job.resumeFromBootloader;
    const maxAttempts = job.target.kind === "app" ? 3 : 2;
    const started = Date.now();
    const took = () => `${Math.round((Date.now() - started) / 1000)} s`;
    for (let attempt = 1; ; attempt++) {
      let reached = fromBootloader ? "connectingBootloader" : "connectingApp";
      patchJob(job.id, {
        status: "running",
        stage: reached,
        progress: null,
        error: null,
        retrying: attempt > 1,
      });
      dfuLog(
        `batch: ${job.name} attempt ${attempt}${
          fromBootloader ? " (from bootloader)" : ""
        }: ${pkg.name}`
      );
      try {
        await updateFirmwareOverBle({
          names: job.target.names,
          // Stock bootloader names are shared, so connect to the scanned unit.
          deviceId: job.target.kind === "stock" ? job.id : null,
          pkg,
          fromBootloader,
          onStage: (s) => {
            reached = s;
            patchJob(job.id, { stage: s });
          },
          onProgress: (p) => patchJob(job.id, { progress: p }),
        });
        dfuLog(`batch: ${job.name} done in ${took()}`);
        patchJob(job.id, {
          status: "done",
          stage: null,
          finishedAt: Date.now(),
          backOn: null,
        });
        return true;
      } catch (e) {
        dfuLog(
          `batch: ${job.name} failed during ${reached} after ${took()}: ${
            e.code || e.name
          } - ${e.message}`
        );
        if (
          fromBootloader &&
          reached === "connectingBootloader" &&
          e.code === "not-found" &&
          job.target.kind === "app"
        ) {
          // Its bootloader has gone: after a failed transfer it boots the old
          // firmware again, so start over from the app.
          fromBootloader = false;
        } else if (reached !== "connectingApp") {
          // Past the reboot the unit is waiting in its bootloader, or (after a
          // dropped transfer) back on its old firmware: the next attempt
          // takes whichever it advertises as.
          fromBootloader = true;
        }
        if (
          attempt >= maxAttempts ||
          FINAL_ERRORS.includes(e.code) ||
          stopRef.current
        ) {
          patchJob(job.id, {
            status: "failed",
            error: e,
            failedStage: reached,
            resumeFromBootloader: fromBootloader,
          });
          return false;
        }
        await sleep(2000);
      }
    }
  };

  // Updated units not yet seen on their new version.
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const unconfirmed = () =>
    (jobsRef.current || []).some((j) => j.status === "done" && !j.backOn);

  // justSent: the unit before this gap finished (its "done" may not have
  // rendered into jobsRef yet).
  const gap = async (justSent) => {
    if (!showVersions || !btAvailable || !(justSent || unconfirmed())) {
      await sleep(BETWEEN_MS);
      return;
    }
    setChecking(true);
    await sleep(QUICK_CHECK_MS);
    setChecking(false);
    // Let the watcher stop before the next unit's scan starts.
    await sleep(200);
  };

  const runBatch = async (list) => {
    let sent = false;
    stopRef.current = false;
    setStopping(false);
    setRunning(true);
    for (let i = 0; i < list.length; i++) {
      if (!aliveRef.current) return;
      if (stopRef.current) {
        patchJob(list[i].id, { status: "skipped" });
        continue;
      }
      if (i > 0) await gap(sent);
      sent = await updateOne(list[i]);
    }
    if (aliveRef.current) {
      setRunning(false);
      setStopping(false);
    }
  };

  const start = () => {
    const list = listed
      .filter((d) => selected.has(d.id))
      // The version it reported before, to show what changed.
      .map((d) => ({
        ...d,
        status: "queued",
        fromVersion: d.advert?.version || null,
      }));
    if (!list.length || !pkg) return;
    stopBleScan();
    setJobs(list);
    runBatch(list);
  };

  const retryFailed = () => {
    const retry = jobs.filter((j) => ["failed", "skipped"].includes(j.status));
    setJobs((prev) =>
      prev.map((j) =>
        retry.some((r) => r.id === j.id)
          ? { ...j, status: "queued", error: null, progress: null }
          : j
      )
    );
    runBatch(retry);
  };

  const stop = () => {
    stopRef.current = true;
    setStopping(true);
  };

  const backToPicker = () => {
    setJobs(null);
    setSelected(new Set());
    scan();
  };

  const counts = (jobs || []).reduce(
    (acc, j) => ({ ...acc, [j.status]: (acc[j.status] || 0) + 1 }),
    {}
  );
  const finished = (counts.done || 0) + (counts.failed || 0);
  const startDisabled = !pkg || selected.size === 0 || btAvailable === false;

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      {/* Header */}
      <div className="mb-5 flex items-center gap-3">
        <button
          onClick={onExit}
          disabled={running}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151] disabled:opacity-40"
          title={t("deviceTools.bleBatch.back")}
          aria-label={t("deviceTools.bleBatch.back")}
        >
          <ChevronLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-[#111827]">
            {t("deviceTools.bleBatch.title")}
          </h2>
          <p className="truncate text-[13px] text-[#6B7280]">
            {t("deviceTools.bleBatch.subtitle")}
          </p>
        </div>
      </div>

      {jobs ? (
        /* ── Batch in progress / finished ── */
        <div className="rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex items-center gap-3 border-b border-[#F3F4F6] px-6 py-4">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#EFF6FF]">
              {running ? (
                <Loader2 size={17} className="animate-spin text-[#2563EB]" />
              ) : (
                <Bluetooth size={17} className="text-[#2563EB]" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#111827]">
                {running
                  ? t("deviceTools.bleBatch.updatingCount", {
                      done: finished,
                      count: jobs.length,
                    })
                  : counts.failed || counts.skipped
                  ? t("deviceTools.bleBatch.summaryWithFailures", {
                      done: counts.done || 0,
                      count: jobs.length,
                    })
                  : t("deviceTools.bleBatch.summaryAllDone", {
                      count: jobs.length,
                    })}
              </p>
              <p className="truncate text-[13px] text-[#6B7280]">
                {showVersions && targetVersion
                  ? t("deviceTools.bleBatch.backOnline", {
                      confirmed: jobs.filter((j) => j.backOn === targetVersion)
                        .length,
                      count: jobs.length,
                      version: pkg.label,
                    })
                  : pkg?.label || pkg?.name}
              </p>
            </div>
          </div>

          <ul>
            {jobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                check={
                  showVersions && targetVersion
                    ? { target: targetVersion, productVersions }
                    : null
                }
              />
            ))}
          </ul>

          <div className="border-t border-[#F3F4F6] px-6 py-4">
            {running ? (
              <div className="flex items-center gap-3">
                <p className="flex-1 text-[13px] text-[#6B7280]">
                  {t("deviceTools.updateFirmware.keepClose")}
                </p>
                <button
                  onClick={stop}
                  disabled={stopping}
                  className={btnSecondary}
                >
                  {stopping
                    ? t("deviceTools.bleBatch.stopping")
                    : t("deviceTools.bleBatch.stopAfterThis")}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {(counts.failed || counts.skipped) > 0 && (
                  <button
                    onClick={retryFailed}
                    className="flex items-center gap-1.5 rounded-[10px] px-4 py-2 text-sm font-semibold text-white transition duration-150"
                    style={primaryGradient}
                  >
                    <RotateCw size={14} />
                    {t("deviceTools.bleBatch.retryFailed", {
                      count: (counts.failed || 0) + (counts.skipped || 0),
                    })}
                  </button>
                )}
                <button onClick={backToPicker} className={btnSecondary}>
                  {t("deviceTools.bleBatch.updateMore")}
                </button>
                <button onClick={onExit} className={`${btnSecondary} ml-auto`}>
                  {t("common.actions.done")}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── Pick a package and wearables ── */
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-6">
            <FirmwareSource
              pkg={pkg}
              onPackage={choosePackage}
              onError={packageFailed}
            />
            {pkgError && (
              <p className="mt-4 text-[13px] text-[#DC2626]">{pkgError}</p>
            )}
          </div>

          <div className="rounded-xl border border-[#F3F4F6] bg-white">
            <div className="flex items-center gap-3 border-b border-[#F3F4F6] px-6 py-4">
              <p className="flex-1 text-[15px] font-semibold text-[#111827]">
                {t("deviceTools.bleBatch.nearbyHeading")}
              </p>
              {offerOutdated ? (
                <button
                  onClick={selectOutdated}
                  className="text-[13px] font-medium text-[#E07A3A] transition duration-150 hover:text-[#C2410C]"
                >
                  {t("deviceTools.bleBatch.selectOutdated", {
                    count: outdatedIds.length,
                    version:
                      pkg.label || versionLabel(targetVersion, productVersions),
                  })}
                </button>
              ) : (
                listed.length > 1 && (
                  <button
                    onClick={toggleAll}
                    className="text-[13px] font-medium text-[#6B7280] transition duration-150 hover:text-[#111827]"
                  >
                    {allSelected
                      ? t("deviceTools.bleBatch.selectNone")
                      : t("deviceTools.bleBatch.selectAll")}
                  </button>
                )
              )}
              <button
                onClick={scanning ? stopBleScan : scan}
                disabled={btAvailable === false}
                className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-[13px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
              >
                {scanning ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t("deviceTools.bleBatch.stopScan")}
                  </>
                ) : (
                  <>
                    <BluetoothSearching size={14} />
                    {t("deviceTools.bleBatch.scan")}
                  </>
                )}
              </button>
            </div>

            {btAvailable === false ? (
              <div className="m-6 flex items-start gap-2 rounded-lg bg-[#FEF3E7] px-3 py-2.5 text-[13px] text-[#9A3412]">
                <BluetoothOff size={15} className="mt-0.5 flex-shrink-0" />
                <span>{t("deviceTools.updateFirmware.bluetoothOff")}</span>
              </div>
            ) : listed.length === 0 ? (
              <div className="px-6 py-10 text-center">
                {scanning && (
                  <Loader2
                    size={18}
                    className="mx-auto mb-3 animate-spin text-[#9CA3AF]"
                  />
                )}
                <p className="text-[14px] font-medium text-[#374151]">
                  {scanning
                    ? t("deviceTools.bleBatch.scanning")
                    : t("deviceTools.bleBatch.noneFound")}
                </p>
                <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed text-[#6B7280]">
                  {t("deviceTools.bleBatch.noneFoundHint")}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[#F3F4F6]">
                {sections.map((section) => (
                  <DeviceSection
                    key={section.key}
                    section={section}
                    devices={listed}
                    selected={selected}
                    expanded={!section.header || expanded.has(section.key)}
                    onToggleExpanded={() => toggleExpanded(section.key)}
                    onToggleDevice={toggle}
                    onToggleGroup={() => toggleGroup(section.ids)}
                    showBattery={showVersions}
                    identify={{
                      run: identify,
                      busyId: identifyingId,
                      results: identified,
                      disabled: scanning || !!identifyingId,
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <button
              onClick={start}
              disabled={startDisabled}
              className="flex w-full items-center justify-center gap-2 rounded-[10px] px-4 py-3.5 text-base font-semibold text-white transition duration-150 disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
              style={startDisabled ? undefined : primaryGradient}
            >
              <Bluetooth size={16} />
              {selected.size === 0
                ? t("deviceTools.bleBatch.startNone")
                : pkg?.label
                ? t("deviceTools.bleBatch.startTo", {
                    count: selected.size,
                    version: pkg.label,
                  })
                : t("deviceTools.bleBatch.start", { count: selected.size })}
            </button>
            {selected.size > 1 && (
              <p className="mt-2.5 text-center text-[13px] text-[#9CA3AF]">
                {t("deviceTools.bleBatch.oneAtATime")}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Checkbox = ({ state }) => (
  <span
    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
      state === "none"
        ? "border-[#D1D5DB] bg-white"
        : "border-[#E07A3A] bg-[#E07A3A]"
    }`}
  >
    {state === "all" && <Check size={13} className="text-white" />}
    {state === "some" && <Minus size={13} className="text-white" />}
  </span>
);

// One version's wearables (or the units in their bootloader): a header that
// selects the group and expands it, then its rows. A section without a header
// is the plain list.
const DeviceSection = ({
  section,
  devices,
  selected,
  expanded,
  onToggleExpanded,
  onToggleDevice,
  onToggleGroup,
  showBattery,
  identify,
}) => {
  const { t } = useTranslation();
  const { header, ids } = section;
  const picked = ids.filter((id) => selected.has(id)).length;
  const rows = devices.filter((d) => ids.includes(d.id));
  return (
    <div>
      {header && (
        <div className="flex items-center gap-3 px-6 py-3.5">
          <button
            onClick={onToggleGroup}
            aria-label={t("deviceTools.bleBatch.selectGroup", {
              group: header.label,
            })}
            aria-pressed={picked === ids.length}
            className="-m-1 p-1"
          >
            <Checkbox
              state={
                picked === 0 ? "none" : picked === ids.length ? "all" : "some"
              }
            />
          </button>
          <button
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <span
              className={`truncate text-[15px] font-semibold ${
                header.current
                  ? "text-[#15803D]"
                  : header.muted
                  ? "text-[#6B7280]"
                  : "text-[#111827]"
              }`}
            >
              {header.label}
            </span>
            {header.build && (
              <span className="text-[13px] text-[#9CA3AF]">
                {t(`deviceTools.bleBatch.builds.${header.build}`)}
              </span>
            )}
            {header.current && (
              <span className="flex items-center gap-1 text-[13px] font-medium text-[#15803D]">
                <Check size={13} />
                {t("deviceTools.bleBatch.upToDate")}
              </span>
            )}
            <span className="ml-auto flex-shrink-0 text-[13px] tabular-nums text-[#6B7280]">
              {picked > 0
                ? t("deviceTools.bleBatch.groupSelected", {
                    selected: picked,
                    count: ids.length,
                  })
                : t("deviceTools.bleBatch.groupCount", { count: ids.length })}
            </span>
            <ChevronDown
              size={16}
              className={`flex-shrink-0 text-[#9CA3AF] transition-transform duration-150 ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      )}
      {expanded && (
        <ul className={header ? "pb-2" : "divide-y divide-[#F3F4F6]"}>
          {rows.map((d) => (
            <DeviceRow
              key={d.id}
              device={d}
              selected={selected.has(d.id)}
              onToggle={() => onToggleDevice(d.id)}
              grouped={!!header}
              showBattery={showBattery}
              identify={identify}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const POWER_STYLE = {
  charging: { Icon: BatteryCharging, className: "text-[#B45309]" },
  charged: { Icon: BatteryFull, className: "text-[#15803D]" },
};

// Battery, and whether the unit is charging or charged (firmware v0.10.0+).
const PowerState = ({ advert }) => {
  const { t } = useTranslation();
  const style = POWER_STYLE[advert.power];
  return (
    <span
      className={`flex flex-shrink-0 items-center gap-1.5 text-[13px] tabular-nums ${
        style?.className || "text-[#6B7280]"
      }`}
    >
      {style && (
        <>
          <style.Icon size={15} />
          <span>{t(`deviceTools.bleBatch.power.${advert.power}`)}</span>
        </>
      )}
      <span className={style ? "text-[#6B7280]" : undefined}>
        {t("deviceTools.bleBatch.battery", { percent: advert.battery })}
      </span>
    </span>
  );
};

const DeviceRow = ({
  device: d,
  selected,
  onToggle,
  grouped,
  showBattery,
  identify,
}) => {
  const { t } = useTranslation();
  return (
    <li
      className={`flex items-center transition duration-150 ${
        selected ? "bg-[#FEF7F3]" : "hover:bg-[#FAFAFA]"
      }`}
    >
      <button
        onClick={onToggle}
        aria-pressed={selected}
        className={`flex min-w-0 flex-1 items-center gap-3 pr-3 text-left ${
          grouped ? "py-2.5 pl-14" : "py-3.5 pl-6"
        }`}
      >
        <Checkbox state={selected ? "all" : "none"} />
        <span className="min-w-0 flex-1">
          <UnitName unit={d} />
          {d.target.kind === "stock" && (
            <span className="block truncate font-mono text-[12px] text-[#9CA3AF]">
              {d.id}
            </span>
          )}
        </span>
        {showBattery && d.advert?.battery != null ? (
          <PowerState advert={d.advert} />
        ) : (
          !grouped && (
            <span
              className={`flex-shrink-0 rounded-md px-2 py-0.5 text-[12px] font-medium ${
                KIND_BADGE[d.target.kind]
              }`}
            >
              {t(`deviceTools.bleBatch.kinds.${d.target.kind}`)}
            </span>
          )
        )}
      </button>
      {d.target.kind === "app" && identify && canClear(d.advert) && (
        <ClearButton device={d} disabled={identify.disabled} />
      )}
      {d.target.kind === "app" && identify && (
        <IdentifyButton device={d} identify={identify} />
      )}
    </li>
  );
};

// Blink a wearable's LED white for 5 s (firmware v0.11.0+, on USB) to find
// it in the case.
const IdentifyButton = ({ device: d, identify }) => {
  const { t } = useTranslation();
  const busy = identify.busyId === d.id;
  const failed = identify.results[d.id] === "failed";
  return (
    <button
      onClick={() => identify.run(d)}
      disabled={identify.disabled}
      title={
        failed
          ? t("deviceTools.bleBatch.identifyFailed")
          : t("deviceTools.bleBatch.identifyHint")
      }
      className={`mr-6 flex flex-shrink-0 items-center gap-1.5 rounded-lg border bg-white px-2.5 py-1 text-[12px] font-medium transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50 ${
        failed
          ? "border-[#FECACA] text-[#DC2626]"
          : "border-[#E5E7EB] text-[#374151]"
      }`}
    >
      {busy ? (
        <Loader2 size={13} className="animate-spin" />
      ) : failed ? (
        <AlertTriangle size={13} />
      ) : (
        <Lightbulb size={13} />
      )}
      {busy
        ? t("deviceTools.bleBatch.identifying")
        : t("deviceTools.bleBatch.identify")}
    </button>
  );
};

// Erase every record on a docked unit (firmware v0.14.0+) through the
// desktop's scanner, after a confirmation: it can't be undone. Its advert
// shows the empty store within a few seconds.
const CLEAR_ERRORS = ["not-found", "refused", "erase-failed", "timeout"];
const ClearButton = ({ device: d, disabled }) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [progress, setProgress] = useState(null);
  const name = d.label ? `${d.label} (${d.name})` : d.name;
  const records = d.advert?.records || 0;

  const clear = async () => {
    setConfirming(false);
    setProgress(null);
    setClearing(true);
    try {
      await clearOverBle({ name: d.name, onProgress: setProgress });
      showToast.success(t("deviceTools.bleBatch.clearDone", { name }));
    } catch (e) {
      const key = CLEAR_ERRORS.includes(e.code) ? e.code : "failed";
      showToast.error(
        t(`deviceTools.bleBatch.clearErrors.${key}`, {
          name,
          error: e.message,
        })
      );
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setConfirming(true)}
        disabled={disabled || clearing}
        title={t("deviceTools.bleBatch.clearHint")}
        className={`relative mr-2 flex flex-shrink-0 items-center gap-1.5 overflow-hidden rounded-lg border bg-white px-2.5 py-1 text-[12px] font-medium transition duration-150 ${
          clearing
            ? "border-[#FECACA] text-[#B91C1C]"
            : "border-[#E5E7EB] text-[#374151] hover:border-[#FECACA] hover:bg-[#FEF2F2] hover:text-[#DC2626] disabled:opacity-50"
        }`}
      >
        {clearing ? (
          <ClearProgress progress={progress} />
        ) : (
          <>
            <Eraser size={13} />
            {t("deviceTools.bleBatch.clear")}
          </>
        )}
      </button>
      <ConfirmDialog
        open={confirming}
        danger
        title={t("deviceTools.bleBatch.clearConfirmTitle", { name })}
        body={t("deviceTools.bleBatch.clearConfirmBody")}
        details={
          records > 0 ? (
            <p className="text-[13px] font-medium text-[#B91C1C]">
              {t("deviceTools.bleBatch.clearConfirmRecords", {
                count: records,
              })}
            </p>
          ) : null
        }
        confirmLabel={t("deviceTools.bleBatch.clearConfirm")}
        onConfirm={clear}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};

// A finished unit's result: back on the package's version, back on another,
// or not seen since (still installing, or off the charger).
const checkStatus = (t, job, check) => {
  if (!check) return null;
  const version = job.backOn;
  if (!version) {
    return {
      text: t("deviceTools.bleBatch.waitingBack"),
      className: "text-[#6B7280]",
    };
  }
  const label = versionLabel(version, check.productVersions);
  return version === check.target
    ? {
        text: t("deviceTools.bleBatch.nowOn", { version: label }),
        className: "text-[#15803D]",
      }
    : {
        text: t("deviceTools.bleBatch.stillOn", { version: label }),
        className: "text-[#B45309]",
      };
};

// The number printed on the wearable's case (from the API) when known, then
// its Bluetooth name.
const UnitName = ({ unit }) => (
  <span className="flex min-w-0 items-baseline gap-2">
    {unit.label && (
      <span className="text-[15px] font-semibold tabular-nums text-[#111827]">
        {unit.label}
      </span>
    )}
    <span
      className={`truncate font-mono ${
        unit.label
          ? "text-[13px] text-[#6B7280]"
          : "text-[14px] font-medium text-[#111827]"
      }`}
    >
      {unit.name}
    </span>
  </span>
);

const JobRow = ({ job, check }) => {
  const { t } = useTranslation();
  const checked = job.status === "done" ? checkStatus(t, job, check) : null;
  const wrongVersion = !!check && !!job.backOn && job.backOn !== check.target;
  const percent = job.progress
    ? Math.floor((job.progress.sent / job.progress.total) * 100)
    : null;
  return (
    <li className="flex items-start gap-3 border-b border-[#F3F4F6] px-6 py-3.5 last:border-b-0">
      <span className="mt-0.5 flex-shrink-0">
        {job.status === "done" && wrongVersion ? (
          <AlertTriangle size={16} className="text-[#D97706]" />
        ) : job.status === "done" ? (
          <CheckCircle2
            size={16}
            className={
              check && !job.backOn ? "text-[#9CA3AF]" : "text-[#16A34A]"
            }
          />
        ) : job.status === "failed" ? (
          <AlertTriangle size={16} className="text-[#DC2626]" />
        ) : job.status === "running" ? (
          <Loader2 size={16} className="animate-spin text-[#E07A3A]" />
        ) : job.status === "skipped" ? (
          <MinusCircle size={16} className="text-[#9CA3AF]" />
        ) : (
          <Circle size={16} className="text-[#D1D5DB]" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2">
          <UnitName unit={job} />
          {check && job.fromVersion && (
            <span className="flex-shrink-0 text-[12px] text-[#9CA3AF]">
              {t("deviceTools.bleBatch.wasVersion", {
                version: versionLabel(job.fromVersion, check.productVersions),
              })}
            </span>
          )}
          <span
            className={`ml-auto flex-shrink-0 text-[13px] ${
              checked?.className || "text-[#6B7280]"
            }`}
          >
            {job.status === "running"
              ? t(`deviceTools.bleBatch.stages.${job.stage}`)
              : checked?.text || t(`deviceTools.bleBatch.status.${job.status}`)}
          </span>
        </p>
        {job.status === "running" &&
          job.stage === "transferring" &&
          job.progress && (
            <div className="mt-1.5">
              <div className="h-1.5 overflow-hidden rounded-full bg-[#F3F4F6]">
                <div
                  className="h-full rounded-full bg-[#E07A3A] transition-all duration-150"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="mt-1 text-[12px] text-[#9CA3AF]">
                {t("deviceTools.updateFirmware.progress", {
                  percent,
                  sent: formatKb(job.progress.sent),
                  total: formatKb(job.progress.total),
                })}
              </p>
            </div>
          )}
        {job.status === "running" && job.retrying && (
          <p className="mt-0.5 text-[12px] text-[#B45309]">
            {t("deviceTools.bleBatch.retrying")}
          </p>
        )}
        {job.status === "failed" && (
          <p className="mt-0.5 text-[13px] leading-relaxed text-[#B91C1C]">
            {describeError(t, job.error, job.failedStage, job.target.names)}
          </p>
        )}
      </div>
    </li>
  );
};

export default BluetoothDfuFlow;
